import React from 'react';
import {
  DataGrid,
} from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    "& .MuiDataGrid-cell:hover": {
        color: "primary.main",
    },
    "& .MuiDataGrid-cell": {
    fontFamily: "Quicksand",
    fontSize: "14px",
    fontWeight: 500,
    color: "#333333",
    },
    "& 	.MuiDataGrid-columnHeader":{
    backgroundColor: "#001D61",
    color: "#FFFFFF",
    fontFamily: "Quicksand",
    fontSize: "12px",
    fontWeight: "bold",
    height: "200px"
    },
    "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-checkboxInput":{
    color: "#FFFFFF",
    },
    "& .MuiDataGrid-columnHeader .MuiSvgIcon-root":{
    color: "#ffffff"
    },
    "& .MuiDataGrid-iconButtonContainer[aria-label*='active filter']":{
    visibility: "visible !important",
    opacity: "1 !important",
    width: "auto !important",
    },
    "& .MuiDataGrid-iconButtonContainer[aria-label*='active filter'] .MuiIconButton-root":{
    visibility: "visible !important",
    opacity: "1 !important",
    padding: "4px",
    },
    "& .MuiDataGrid-iconButtonContainer[aria-label*='active filter'] .MuiSvgIcon-root":{
    color: "#ffffff",
    opacity: "1 !important",
    }
}));

const SERVER_FILTER_DEBOUNCE_MS = 450;
const DEFAULT_FILTER_MODEL = { items: [] };
const serverFilterModelStore = new Map();

const hasFilterInputValue = (filterModel) => {
    const items = Array.isArray(filterModel?.items) ? filterModel.items : [];

    return items.some((item) => {
        const value = Array.isArray(item.value) ? item.value[0] : item.value;
        return value !== null && value !== undefined && String(value).trim() !== "";
    });
};

const hasDraftFilterItem = (filterModel) => {
    const items = Array.isArray(filterModel?.items) ? filterModel.items : [];
    return items.length > 0;
};

const getFirstFilterField = (filterModel) => {
    const items = Array.isArray(filterModel?.items) ? filterModel.items : [];
    const item = items[0];

    return item ? item.columnField || item.field || "" : "";
};

const isEmptyDraftForDifferentColumn = (nextModel, currentModel) => {
    const nextField = getFirstFilterField(nextModel);
    const currentField = getFirstFilterField(currentModel);

    return (
        hasFilterInputValue(currentModel) &&
        hasDraftFilterItem(nextModel) &&
        !hasFilterInputValue(nextModel) &&
        nextField &&
        currentField &&
        nextField !== currentField
    );
};

const buildFilterStoreKey = (columns = [], explicitKey) => {
    if (explicitKey) {
        return explicitKey;
    }

    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const columnKey = Array.isArray(columns)
        ? columns.map((column) => column.field).filter(Boolean).join("|")
        : "";

    return `${path}:${columnKey}`;
};

const CustomDataGrid = React.forwardRef(function CustomDataGrid(props, ref) {
    const {
        filterMode,
        filterModel,
        filterPersistenceKey,
        columns,
        page,
        onFilterModelChange,
        onPageChange,
        ...rest
    } = props;
    const filterStoreKey = React.useMemo(
        () => buildFilterStoreKey(columns, filterPersistenceKey),
        [columns, filterPersistenceKey]
    );
    const isServerFilter = filterMode === 'server';
    const isFilterModelControlled = filterModel !== undefined;
    const [internalFilterModel, setInternalFilterModel] = React.useState(() => (
        isServerFilter && serverFilterModelStore.has(filterStoreKey)
            ? serverFilterModelStore.get(filterStoreKey)
            : DEFAULT_FILTER_MODEL
    ));
    const suppressFilterPageResetRef = React.useRef(false);
    const suppressFilterPageResetTimerRef = React.useRef(null);
    const serverFilterDebounceTimerRef = React.useRef(null);

    React.useEffect(() => {
        if (!isServerFilter || isFilterModelControlled) {
            return;
        }

        setInternalFilterModel(
            serverFilterModelStore.has(filterStoreKey)
                ? serverFilterModelStore.get(filterStoreKey)
                : DEFAULT_FILTER_MODEL
        );
    }, [filterStoreKey, isFilterModelControlled, isServerFilter]);

    React.useEffect(() => {
        return () => {
            if (suppressFilterPageResetTimerRef.current) {
                clearTimeout(suppressFilterPageResetTimerRef.current);
            }
            if (serverFilterDebounceTimerRef.current) {
                clearTimeout(serverFilterDebounceTimerRef.current);
            }
        };
    }, []);

    const markFilterPageReset = React.useCallback(() => {
        suppressFilterPageResetRef.current = true;
        if (suppressFilterPageResetTimerRef.current) {
            clearTimeout(suppressFilterPageResetTimerRef.current);
        }
        suppressFilterPageResetTimerRef.current = setTimeout(() => {
            suppressFilterPageResetRef.current = false;
        }, 750);
    }, []);

    const updateInternalServerFilterModel = React.useCallback((model) => {
        if (!isServerFilter || isFilterModelControlled) {
            return;
        }

        setInternalFilterModel(model);

        if (!hasDraftFilterItem(model)) {
            serverFilterModelStore.delete(filterStoreKey);
            return;
        }

        serverFilterModelStore.set(filterStoreKey, model);
    }, [filterStoreKey, isFilterModelControlled, isServerFilter]);

    const handleFilterModelChange = React.useCallback((model, details) => {
        if (isServerFilter) {
            markFilterPageReset();

            if (isFilterModelControlled) {
                if (hasDraftFilterItem(model) && !hasFilterInputValue(model)) {
                    if (hasFilterInputValue(filterModel) && !isEmptyDraftForDifferentColumn(model, filterModel)) {
                        if (onFilterModelChange) {
                            onFilterModelChange(DEFAULT_FILTER_MODEL, details);
                        }
                        return;
                    }

                    if (onFilterModelChange) {
                        onFilterModelChange(model, details);
                    }
                    return;
                }

                if (onFilterModelChange) {
                    onFilterModelChange(model, details);
                }
                return;
            }

            if (serverFilterDebounceTimerRef.current) {
                clearTimeout(serverFilterDebounceTimerRef.current);
                serverFilterDebounceTimerRef.current = null;
            }

            if (hasDraftFilterItem(model) && !hasFilterInputValue(model)) {
                if (hasFilterInputValue(internalFilterModel)) {
                    if (isEmptyDraftForDifferentColumn(model, internalFilterModel)) {
                        updateInternalServerFilterModel(model);
                        return;
                    }

                    updateInternalServerFilterModel(DEFAULT_FILTER_MODEL);
                    if (onFilterModelChange) {
                        onFilterModelChange(DEFAULT_FILTER_MODEL, details);
                    }
                    return;
                }

                updateInternalServerFilterModel(model);
                return;
            }

            if (hasFilterInputValue(model)) {
                updateInternalServerFilterModel(model);
                serverFilterDebounceTimerRef.current = setTimeout(() => {
                    if (onFilterModelChange) {
                        onFilterModelChange(model, details);
                    }
                }, SERVER_FILTER_DEBOUNCE_MS);
                return;
            }

            updateInternalServerFilterModel(model);
        }

        if (onFilterModelChange) {
            onFilterModelChange(model, details);
        }
    }, [filterModel, internalFilterModel, isFilterModelControlled, isServerFilter, markFilterPageReset, onFilterModelChange, updateInternalServerFilterModel]);

    const handlePageChange = React.useCallback((newPage, details) => {
        if (newPage === page) {
            return;
        }

        if (filterMode === 'server' && newPage === 0 && suppressFilterPageResetRef.current) {
            suppressFilterPageResetRef.current = false;
            return;
        }

        if (onPageChange) {
            onPageChange(newPage, details);
        }
    }, [filterMode, onPageChange, page]);

    return (
        <StyledDataGrid
            ref={ref}
            filterMode={filterMode}
            filterModel={isServerFilter && !isFilterModelControlled ? internalFilterModel : filterModel}
            columns={columns}
            page={page}
            onFilterModelChange={handleFilterModelChange}
            onPageChange={handlePageChange}
            {...rest}
        />
    );
});

CustomDataGrid.defaultProps = {
    rowsPerPageOptions: [5, 10, 25, 100],
};

export default CustomDataGrid
